<script>
import { required } from "@vuelidate/validators";
import { password } from "@/utils/form-validators";
import Button from "@/components/atoms/button";
import FormInput from "@/components/molecules/form-input";
import FormGroup from "@/components/molecules/form-group";
import Form from "@/components/molecules/form";
import FormFieldset from "@/components/molecules/form-fieldset";
import Legend from "@/components/molecules/form-fieldset/legend";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import StateOverlay from "@/components/molecules/state-overlay";
import PasswordRequirements from "@/components/organisms/inscription-form/password-requirements";
import Titre from "@/components/atoms/titre";
import LazyImage from "@/components/atoms/lazy-image";
import modificationMdp from "@/assets/images/modification-mdp.svg";

export default {
  name: "ModifierMotDePasse",
  components: {
    Button,
    FormInput,
    FormGroup,
    Form,
    FormFieldset,
    Legend,
    StateOverlay,
    PasswordRequirements,
    Titre,
    LazyImage
  },
  props: {
    isLoading: {
      type: Boolean,
      required: true
    },
    isObsolete: {
      type: Boolean,
      required: false,
      default: false
    },
    ...formValidationProps()
  },
  emits: ["save"],
  setup(props) {
    const { v$, getState, getErrorMessage, checkValidation } =
      useFormValidation(props);
    return {
      v$,
      getState,
      getErrorMessage,
      checkValidation,
      modificationMdp
    };
  },
  data() {
    return {
      model: {
        old_password: "",
        password: "",
        password_confirmation: ""
      }
    };
  },
  validations() {
    return {
      model: {
        old_password: {
          required
        },
        password: {
          required,
          password
        },
        password_confirmation: {
          required,
          password,
          isValidConfirmation(value) {
            return this.model.password === value;
          }
        }
      }
    };
  },
  methods: {
    save() {
      if (this.checkValidation(this.v$)) {
        this.$emit("save", this.model);
      }
    }
  }
};
</script>

<template>
  <div class="u-mx-sm">
    <template v-if="isObsolete">
      <div class="u-text-center">
        <Titre
          tag="h2"
          font="secondary"
          variant="primary"
        >
          Votre mot de passe se fait vieux !
        </Titre>
        <p class="u-mt-sm">
          Pour améliorer la protection de votre compte,
          nous vous invitons à créer un nouveau mot de passe suffisamment sécurisé.
        </p>
        <LazyImage
          :src="modificationMdp"
          width="125"
          alt=""
          class="u-flex-shrink-none u-mt-sm"
        />
      </div>
    </template>
    <template v-else>
      <h1 class="u-color-primary u-font-24 u-mb-md u-text-center">
        Modifier votre mot de passe
      </h1>
      <p class="u-text-center">
        Votre nouveau mot de passe sera utilisé pour accéder à vos différents comptes Cora.
      </p>
    </template>
    <Form
      class="u-mt-md"
      :error="error"
      @submit.prevent="save"
    >
      <StateOverlay
        :is-loading="isLoading"
        overlay
      >
        <FormFieldset
          center
          size="lg"
        >
          <Legend :class="isObsolete ? 'u-mb-md' : 'u-mb-sm'">
            <span :class="isObsolete ? 'u-font-18' : 'u-font-21'">
              <template v-if="isObsolete">
                Votre ancien mot de passe
              </template>
              <template v-else>
                Votre mot de passe actuel
              </template>
            </span>
          </Legend>
          <FormGroup
            size="lg"
            :state="getState(v$,'old_password', 'model')"
          >
            <FormInput
              v-model="model.old_password"
              size="lg"
              type="password"
              :state="getState(v$,'old_password', 'model')"
              @blur="v$.model.old_password.$touch()"
              @input="v$.model.old_password.$reset()"
            >
              <template v-if="isObsolete">
                Mot de passe *
              </template>
              <template v-else>
                Mot de passe actuel
              </template>
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("old_password","Merci de renseigner votre mot de passe actuel") }}
              </p>
            </template>
          </FormGroup>
        </FormFieldset>
        <FormFieldset
          center
          size="lg"
          class="u-mb-md u-mt-md"
        >
          <Legend :class="isObsolete ? 'u-mb-md' : 'u-mb-sm'">
            <span :class="isObsolete ? 'u-font-18' : 'u-font-21'">
              <template v-if="isObsolete">
                Votre nouveau mot de passe sécurisé
              </template>
              <template v-else>
                Votre nouveau mot de passe
              </template>
            </span>
          </Legend>

          <FormGroup
            class="u-mb-sm"
            size="lg"
            :state="getState(v$,'password', 'model')"
          >
            <FormInput
              v-model="model.password"
              size="lg"
              type="password"
              :state="getState(v$,'password', 'model')"
              @blur="v$.model.password.$touch()"
              @input="v$.model.password.$reset()"
            >
              <template v-if="isObsolete">
                Mot de passe *
              </template>
              <template v-else>
                Votre nouveau mot de passe
              </template>
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("password","Merci de renseigner votre nouveau mot de passe") }}
              </p>
            </template>
          </FormGroup>
          <FormGroup
            size="lg"
            :state="getState(v$,'password_confirmation', 'model')"
          >
            <FormInput
              v-model="model.password_confirmation"
              size="lg"
              type="password"
              :state="getState(v$,'password_confirmation', 'model')"
              @blur="v$.model.password_confirmation.$touch()"
              @input="v$.model.password_confirmation.$reset()"
            >
              <template v-if="isObsolete">
                Confirmer le mot de passe *
              </template>
              <template v-else>
                Confirmez le nouveau mot de passe
              </template>
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("password_confirmation","Merci de renseigner un mot de passe valide et identique") }}
              </p>
            </template>
          </FormGroup>
          <div class="u-mt-sm">
            <p class="u-mr-lg u-mb-xs">
              Pour garantir la sécurité de votre compte, votre mot de passe doit contenir au minimum :
            </p>
            <PasswordRequirements :password="model.password" />
          </div>
        </FormFieldset>
        <div class="u-text-center u-mt-lg u-mb-lg">
          <Button
            variant="primary"
            radius="full"
            size="md"
            type="submit"
            value="Mettre à jour mon mot de passe"
          />
        </div>
      </StateOverlay>
    </Form>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/modifier-mdp-form.scss";
</style>
